<template>
    <main class="resource-page" :class="[`resource-page--${theme}`, {'resource-page--has-aside': hasAside}]">
        <div class="resource-page__header" data-testid="resource-page-header">
            <slot name="header" />
        </div>
        <div class="resource-page__content">
            <div v-if="hasAside" class="resource-page__aside" data-testid="resource-page-aside">
                <aside ref="aside" class="resource-page__aside__sticky">
                    <slot name="aside" />
                </aside>
            </div>
            <section class="resource-page__main" data-testid="resource-page-main">
                <slot name="main" />
            </section>
        </div>
    </main>
</template>

<script lang="ts" setup>
    import {computed, useSlots} from 'vue';

    const slots = useSlots();
    const hasAside = computed(() => slots.aside !== undefined);

    withDefaults(
        defineProps<{
            theme?: 'default' | 'corporate' | 'strategy';
        }>(),
        {
            theme: 'default',
        },
    );
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    /* Layout */
    .resource-page {
        &__content {
            margin-top: $margin-section-mobile;
            margin-bottom: $margin-section-mobile;

            .slice-content {
                padding: 0;
            }

            @include content-width;

            > :first-child {
                margin-bottom: var(--joy-core-spacing-9);
            }
        }

        &__main {
            max-width: 824px;
            min-width: 0;

            .joy-button {
                white-space: wrap;
            }
        }

        @include mq.screen_md(min) {
            .resource-page--has-aside & {
                &__content {
                    display: flex;
                    flex-flow: row-reverse;
                    margin-top: $margin-section-normal;
                    margin-bottom: $margin-section-normal;
                    gap: var(--joy-core-spacing-11);
                }

                &__main {
                    flex: 1;

                    .joy-button {
                        white-space: nowrap;
                    }
                }

                &__aside {
                    max-width: unset;
                    flex: none;
                    width: 360px;

                    &__sticky {
                        position: sticky;
                        top: 92px;
                    }
                }
            }
        }

        @include mq.screen_lg(min) {
            .resource-page--has-aside & {
                &__aside {
                    width: 400px;
                }
            }
        }
    }

    /* Themes */
    .resource-page {
        /* Background */
        &--default {
            .resource-page__hero {
                background-color: var(--joy-color-quaternary-10);
            }
        }

        &--corporate {
            .resource-page__hero {
                background-color: var(--joy-color-neutral-20);
            }
        }

        &--strategy {
            .resource-page__hero {
                background-color: var(--joy-color-neutral-20);
            }

            /* Buttons */
            .joy-button_main {
                background-color: var(--joy-color-secondary-30);

                &:hover {
                    background-color: var(--joy-color-secondary-50);
                }
            }
        }

        /* Links */
        .resource-page__hero .document-info,
        .resource-page__main {
            a:not(.joy-button, .category-pill a) {
                color: var(--joy-color-primary-50);
                text-decoration: underline currentcolor;

                &:hover {
                    color: var(--joy-color-primary-70);
                }
            }
        }

        &--strategy {
            .resource-page__hero .document-info,
            .resource-page__main {
                /* Links */
                a:not(.joy-button, .category-pill a) {
                    color: var(--joy-color-secondary-30);

                    &:hover {
                        color: var(--joy-color-secondary-50);
                    }
                }

                /* List bullets */
                .list-slice li::before {
                    content: url("data:image/svg+xml,%3csvg%20width='22'%20height='22'%20viewBox='0%200%2025%2025'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cellipse%20cx='12.5002'%20cy='12.5'%20rx='11.1111'%20ry='11.1111'%20stroke='%23035266'%20stroke-width='1.33333'/%3e%3cpath%20d='M8.79639%2014.2279L10.0603%2015.9127C10.1289%2016.0046%2010.2184%2016.079%2010.3214%2016.1296C10.4244%2016.1802%2010.538%2016.2056%2010.6527%2016.2037C10.7675%2016.2018%2010.8801%2016.1726%2010.9814%2016.1187C11.0827%2016.0647%2011.1697%2015.9874%2011.2352%2015.8933L13.7195%2012.3448L14.9616%2010.5706L16.2038%208.79639'%20stroke='%23035266'%20stroke-width='1.33333'%20stroke-linecap='round'%20stroke-linejoin='round'/%3e%3c/svg%3e");
                }
            }
        }
    }
</style>
